import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { priceChecksApi } from '../services/api';
import Navbar from '../components/Navbar';
import ConfirmModal from '../components/ConfirmModal';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const emptyForm = {
  name: '',
  url: '',
  frequency: 'daily',
  time: '09:00',
  timezone: 'UTC',
  day_of_week: 1,
  email_notify: false,
  email_address: '',
  telegram_notify: false,
  alert_on_change: true,
};

const PriceChecks = () => {
  const [checks, setChecks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await priceChecksApi.getAll();
      setChecks(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load price checks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (check) => {
    setEditing(check);
    setForm({
      name: check.name,
      url: check.url,
      frequency: check.frequency,
      time: check.time || '09:00',
      timezone: check.timezone || 'UTC',
      day_of_week: check.day_of_week ?? 1,
      email_notify: Boolean(check.email_notify),
      email_address: check.email_address || '',
      telegram_notify: Boolean(check.telegram_notify),
      alert_on_change: check.alert_on_change !== 0,
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        day_of_week: form.frequency === 'weekly' ? form.day_of_week : null,
        time: form.frequency === 'hourly' ? null : form.time,
      };
      if (editing) {
        await priceChecksApi.update(editing.id, payload);
      } else {
        await priceChecksApi.create(payload);
      }
      setShowForm(false);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.details?.join(', ') || err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirmId(null);
    try {
      await priceChecksApi.delete(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleRunNow = async (id) => {
    try {
      await priceChecksApi.runNow(id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Run now failed');
    }
  };

  const field = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  const toggle = (key) => () => setForm(f => ({ ...f, [key]: !f[key] }));

  return (
    <div className="min-h-screen bg-gray-50">
      {confirmId && (
        <ConfirmModal
          message="Delete this price check?"
          onConfirm={() => handleDelete(confirmId)}
          onCancel={() => setConfirmId(null)}
        />
      )}
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Price Checks</h1>
          <button
            onClick={openAdd}
            className="w-10 h-10 bg-primary-600 text-white rounded-full shadow-md hover:bg-primary-700 transition flex items-center justify-center"
            title="New Price Check"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>

        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 cursor-pointer text-sm"
            onClick={() => setError('')}
          >
            {error}
          </div>
        )}

        {showForm && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editing ? 'Edit Price Check' : 'New Price Check'}
            </h2>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={field('name')}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g. PS5 Console"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                  <input
                    type="url"
                    value={form.url}
                    onChange={field('url')}
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="https://www.amazon.co.uk/..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                  <select
                    value={form.frequency}
                    onChange={field('frequency')}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekdays">Weekdays</option>
                    <option value="weekly">Weekly</option>
                  </select>
                </div>
                {form.frequency !== 'hourly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                    <input
                      type="time"
                      value={form.time}
                      onChange={field('time')}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}
                {form.frequency === 'weekly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                    <select
                      value={form.day_of_week}
                      onChange={(e) => setForm(f => ({ ...f, day_of_week: Number(e.target.value) }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  <input
                    type="text"
                    value={form.timezone}
                    onChange={field('timezone')}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="UTC"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.email_notify}
                    onChange={toggle('email_notify')}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Email notify</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.telegram_notify}
                    onChange={toggle('telegram_notify')}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Telegram notify</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.alert_on_change}
                    onChange={toggle('alert_on_change')}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Alert on change only</span>
                </label>
              </div>

              {form.email_notify && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                  <input
                    type="email"
                    value={form.email_address}
                    onChange={field('email_address')}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="you@example.com"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 transition disabled:opacity-60"
                >
                  {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditing(null); }}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        ) : checks.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            No price checks yet. Click + to add one.
          </div>
        ) : (
          <div className="space-y-3">
            {checks.map(check => (
              <div key={check.id} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm truncate">{check.name}</h3>
                    <a
                      href={check.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-primary-600 hover:underline truncate block"
                    >
                      {check.url}
                    </a>
                    <div className="flex flex-wrap gap-1.5 mt-1.5">
                      <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {check.frequency === 'weekdays' ? 'Weekdays' : check.frequency}
                      </span>
                      {check.time && check.frequency !== 'hourly' && (
                        <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                          {check.frequency === 'weekly' && check.day_of_week != null
                            ? DAYS[check.day_of_week] + ' '
                            : ''}{check.time}
                        </span>
                      )}
                      {check.last_price != null && (
                        <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-semibold">
                          {check.last_price}
                        </span>
                      )}
                      {check.last_in_stock != null && (
                        <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-semibold ${
                          check.last_in_stock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {check.last_in_stock ? 'In Stock' : 'Out of Stock'}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-4 mt-1.5 text-xs text-gray-400">
                      <span>Last: {check.last_run_time ? new Date(check.last_run_time).toLocaleString() : 'Never'}</span>
                      <span>Next: {check.next_run_time ? new Date(check.next_run_time).toLocaleString() : '—'}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 items-end shrink-0">
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => handleRunNow(check.id)}
                        className="text-xs px-2 py-1 border border-green-300 text-green-600 rounded hover:bg-green-500 hover:text-white transition"
                      >
                        Run
                      </button>
                      <button
                        onClick={() => openEdit(check)}
                        className="text-xs px-2 py-1 border border-gray-300 text-gray-600 rounded hover:bg-gray-100 transition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setConfirmId(check.id)}
                        className="text-xs px-2 py-1 border border-red-200 text-red-500 rounded hover:bg-red-500 hover:text-white transition"
                      >
                        Delete
                      </button>
                    </div>
                    <Link
                      to={`/price-checks/${check.id}`}
                      className="text-xs text-primary-600 hover:underline"
                    >
                      View Results
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceChecks;
